import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type {
  AcceptOfferPayload,
  AcceptOfferResult,
  BookingActionPayload,
  CreateBookingPayload,
  CreateBookingResult,
  CreateRequestPayload,
  CreateRequestResult,
  DisputeBookingPayload,
  PayBookingResult,
  RedeemPromoCodePayload,
  RedeemPromoCodeResult,
  ReportUserPayload,
  ReviewLongevityPayload,
  ServiceCategory,
  SubmitOfferPayload,
  SubmitReviewPayload,
  VerifyStudentPayload,
  OcrStudentCardPayload,
  OcrStudentCardResult,
} from '@makhe/shared';

const call = <In = unknown, Out = unknown>(name: string) =>
  httpsCallable<In, Out>(functions, name);

type Ok = { ok: boolean };
type CancelResult = Ok & { refundZARcents?: number; penaltyZARcents?: number };

export const callCreateBooking = call<CreateBookingPayload, CreateBookingResult>(
  'createBooking',
);
export const callAcceptBooking = call<BookingActionPayload, Ok>('acceptBooking');
export const callDeclineBooking = call<BookingActionPayload, Ok>('declineBooking');
export const callCancelBooking = call<BookingActionPayload, CancelResult>('cancelBooking');
export const callProposeBookingTime = call<
  { bookingId: string; scheduledStart: number; note?: string },
  Ok
>('proposeBookingTime');
export const callAcceptCounterProposal = call<BookingActionPayload, Ok>('acceptCounterProposal');
export const callMarkInProgress = call<BookingActionPayload, Ok>('markBookingInProgress');
export const callMarkServiceDone = call<BookingActionPayload, Ok>('markServiceDone');
export const callConfirmCompleted = call<BookingActionPayload, Ok>('confirmServiceCompleted');
export const callDisputeBooking = call<DisputeBookingPayload, Ok>('disputeBooking');

export const callSubmitReview = call<SubmitReviewPayload, Ok & { reviewId: string }>(
  'submitReview',
);
export const callRespondReviewLongevity = call<ReviewLongevityPayload, Ok>(
  'respondReviewLongevity',
);
export const callUpdateSellerCategories = call<{ categories: ServiceCategory[] }, Ok>(
  'updateSellerCategories',
);
// Admin: (re)mirror every user into publicProfiles. One-time after the privacy
// migration; idempotent, safe to re-run if the mirror ever drifts.
export const callBackfillPublicProfiles = call<unknown, Ok & { written: number }>(
  'backfillPublicProfiles',
);
export const callPayBooking = call<{ bookingId: string; origin?: string }, PayBookingResult>('payBooking');
export const callVerifyPayment = call<
  { bookingId: string; reference: string },
  { ok: boolean; status: string }
>('verifyPayment');

// Service requests (reverse marketplace)
export const callCreateRequest = call<CreateRequestPayload, CreateRequestResult>('createRequest');
export const callCancelRequest = call<{ requestId: string }, Ok>('cancelRequest');
export const callExtendRequest = call<{ requestId: string }, Ok>('extendRequest');
export const callSubmitOffer = call<SubmitOfferPayload, Ok & { offerId: string }>('submitOffer');
export const callWithdrawOffer = call<{ requestId: string }, Ok>('withdrawOffer');
export const callAcceptOffer = call<AcceptOfferPayload, AcceptOfferResult>('acceptOffer');
export const callRedeemPromoCode = call<RedeemPromoCodePayload, RedeemPromoCodeResult>(
  'redeemPromoCode',
);

export const callRecordReferral = call<{ referrerUid: string }, Ok>('recordReferral');
export const callBootstrapUserProfile = call<unknown, Ok & { repaired: boolean }>(
  'bootstrapUserProfile',
);
export const callRecordLegalConsent = call<
  { termsVersion: string; privacyVersion: string },
  Ok
>('recordLegalConsent');
export const callCompleteSellerOnboarding = call<
  import('@makhe/shared').CompleteSellerOnboardingPayload,
  Ok
>('completeSellerOnboarding');
export const callConfirmSafetyCheckIn = call<BookingActionPayload, Ok & { complete?: boolean }>(
  'confirmSafetyCheckIn',
);
export const callRaisePanic = call<
  import('@makhe/shared').RaisePanicPayload,
  Ok & { incidentId: string }
>('raisePanic');
export const callAdminAckPanic = call<{ incidentId: string }, Ok>('adminAckPanic');
export const callVerifyStudent = call<VerifyStudentPayload, Ok>('verifyStudent');
export const callOcrStudentCard = call<OcrStudentCardPayload, OcrStudentCardResult>('ocrStudentCard');

export const callReportUser = call<ReportUserPayload, Ok & { reportId: string }>(
  'reportUser',
);
// Returns the full POPIA data export object (user + bookings + reviews +
// notifications + reports), which the client serialises to a downloadable JSON
// file — not a { downloadURL } wrapper.
export const callExportMyData = call<unknown, Record<string, unknown>>('exportMyData');
export const callUpdateBankAccount = call<
  { accountHolderName: string; bank: string; accountNumber: string; branchCode: string },
  Ok & { masked: string }
>('updateBankAccount');

export const callAdminBroadcast = call<
  { title: string; body: string; audience: 'all' | 'buyers' | 'sellers' | 'specific_users'; specificUserIds?: string[] },
  Ok & { announcementId: string; recipientCount: number }
>('adminBroadcast');
export const callAdminResolveReport = call<
  { reportId: string; resolution: string; adminNotes?: string },
  Ok
>('adminResolveReport');
export const callAdminReviewVerification = call<
  {
    uid: string;
    approve: boolean;
    institution?: string;
    reason?: string;
    applicationId?: string;
  },
  Ok & { userUpdated: boolean }
>('adminReviewVerification');
export const callAdminRequestVerificationInfo = call<
  { uid: string; note?: string; applicationId?: string },
  Ok & { userUpdated: boolean }
>('adminRequestVerificationInfo');
export const callAdminUserTrustAction = call<
  { uid: string; action: 'warn' | 'suspend' | 'unsuspend' | 'ban' | 'unban'; days?: number; note?: string },
  Ok
>('adminUserTrustAction');
export const callAdminRevokeSeller = call<{ uid: string; reason?: string }, Ok & { servicesRemoved: number }>(
  'adminRevokeSeller',
);
export const callAdminMarkPayoutReleased = call<{ bookingId: string; note?: string }, Ok>(
  'adminMarkPayoutReleased',
);

export const callAddFCMToken = call<{ token: string }, Ok>('addFCMToken');
export const callRemoveFCMToken = call<{ token: string }, Ok>('removeFCMToken');
